import { useItemUpsellFeatureABTesting } from '@olo-web/utils/common/hooks/useItemUpsellFeatureABTesting';
import { dynamicSort } from '@olo-web/utils/common/functions/dynamicSort';
import { useMenu } from '@olo-web/domain/menu/queries';
import { useOrder } from '@olo-web/domain/orders/queries/useOrder';
import { useMerchant } from '@domain/merchants/queries/useMerchant';
import { UseQueryResult, useQuery, UseQueryOptions } from 'react-query';
import axios, { AxiosError } from 'axios';
import { useCurrentMerchantDayJs } from '@domain/merchants/hooks/useCurrentMerchantDayJs';

import { ENVIRONMENTS, getEnvironment } from '@olo-web/utils/common/functions';
import { useIsDineIn } from '@olo-web/utils/common/hooks';

type TItemRecommendation = {
  menu_item_location_id: string;
  list_position: number;
  rule_based: boolean;
};

type TItemListObject = {
  menu_item_location_id: string;
  item_quantity: string;
  item_gross_total: string;
  registration_utc_timestamp: string;
};

const itemUpsellUrl = process.env.NEXT_PUBLIC_SPOTON_PLUS_URL || '';

export const getRecommendedItems = async (
  merchantId: string,
  orderId: string,
  itemIdsArray: TItemListObject[],
  currentMerchantTime: string,
  isSplashScreen: boolean
): Promise<TItemRecommendation[]> => {
  try {
    const body = {
      pos_location_id: merchantId,
      pos_order_id: orderId,
      items_list: itemIdsArray,
      request_recommendation: true,
      local_timestamp: currentMerchantTime,
      order_channel: 'ord',
      mocked_response: getEnvironment() === ENVIRONMENTS.QA,
    };
    const options: { timeout?: number } = {};
    if (isSplashScreen) {
      options.timeout = 500;
    }
    const { data } = await axios.post(`${itemUpsellUrl}/api/recommendations`, body, options);
    return data;
  } catch (error) {
    console.error(error);
    throw new Error(error?.response?.data?.detail);
  }
};

export const useRecommendedItems = (
  options?: UseQueryOptions<TItemRecommendation[], AxiosError, IMenuItem[]>
): UseQueryResult<IMenuItem[], AxiosError> => {
  const { data: merchant } = useMerchant();
  const { data: order } = useOrder();
  const { data: menu } = useMenu();
  const { isABTestingEnabled } = useItemUpsellFeatureABTesting();
  const currentMerchantDayJs = useCurrentMerchantDayJs();
  const isDineIn = useIsDineIn();
  const { showSplashScreen } = useItemUpsellFeatureABTesting();

  const flattenedMenu: IMenuItem[] = menu?.reduce((a, c) => [...a, ...c.menuItems], []) || [];

  const itemIdsArray = order?.items?.map((item) => ({
    menu_item_location_id: item?.menuItemLocationId,
    item_quantity: item?.quantity,
    item_gross_total: item?.displayAmount,
    registration_utc_timestamp: item?.createdDateTime,
  }));
  const itemIdsArrayKey = order?.items?.map((item) => item.menuItemLocationId);
  const isEnabled =
    !isDineIn && !!(isABTestingEnabled && merchant?.merchantId && order?.id && itemIdsArray.length);

  return useQuery(
    ['item-recommendations', order?.id, itemIdsArrayKey],
    () =>
      getRecommendedItems(
        merchant?.merchantId,
        order?.id,
        itemIdsArray,
        currentMerchantDayJs.convertToMerchantTime(new Date()).toISOString(),
        showSplashScreen
      ),
    {
      staleTime: 100000,
      enabled: isEnabled,
      retry: 1,
      select: (data) => {
        if (!data.length) return [];
        return data
          .sort(dynamicSort('list_position'))
          .map((rec) =>
            flattenedMenu.find((item) => item.menuItemLocationId === rec.menu_item_location_id)
          )
          .filter(Boolean);
      },
      ...options,
    }
  );
};
